import { api } from "./client";
import type {
  AdminProvider,
  AdminUser,
  AnalyticsOverview,
  AuditEvent,
  Category,
  RegionOptions,
  HomeSection,
  GeoLocationHit,
  KycDetail,
  ReviewService,
  ServiceReviewStatus,
  LoginResult,
  ServiceAreaCheck,
  ServiceAreaOverview,
  SettingHistoryRow,
  SettingRow,
  StaffAccount,
  StaffMe,
  Ticket,
  TicketStats,
  TokenResponse,
  UserStatus,
  VerificationStatus,
} from "./types";

export const auth = {
  login: (email: string, password: string) =>
    api.anonymousPost<LoginResult>("/admin/auth/login", { email, password }),
  verifyTotp: (challenge_token: string, code: string) =>
    api.anonymousPost<TokenResponse>("/admin/auth/totp", { challenge_token, code }),
  me: () => api.get<StaffMe>("/admin/auth/me"),
  logout: (refresh_token: string) =>
    api.post<void>("/admin/auth/logout", { refresh_token }),
  changePassword: (current_password: string, new_password: string) =>
    api.post<void>("/admin/auth/password", { current_password, new_password }),
  enrollTotp: () =>
    api.post<{ secret: string; provisioning_uri: string }>("/admin/auth/totp/enroll"),
  confirmTotp: (code: string) =>
    api.post<void>("/admin/auth/totp/confirm", { challenge_token: "", code }),
};

export const analytics = {
  overview: (params: {
    date_from?: string;
    date_to?: string;
    state?: string;
    city?: string;
    region_limit?: number;
  }) => api.get<AnalyticsOverview>("/admin/analytics/overview", params),
  regions: () => api.get<RegionOptions>("/admin/analytics/regions"),
};

export const users = {
  list: (params: {
    page?: number;
    page_size?: number;
    q?: string;
    user_status?: UserStatus | "";
  }) => api.list<AdminUser>("/admin/users", params),
  get: (id: string) => api.get<AdminUser>(`/admin/users/${id}`),
  setStatus: (id: string, status: UserStatus, reason?: string) =>
    api.patch<AdminUser>(`/admin/users/${id}/status`, { status, reason }),
};

export const providers = {
  list: (params: {
    page?: number;
    page_size?: number;
    q?: string;
    verification_status?: VerificationStatus | "";
    city?: string;
  }) => api.list<AdminProvider>("/admin/providers", params),
  get: (id: string) => api.get<AdminProvider>(`/admin/providers/${id}`),
};

export const kyc = {
  queue: (params: {
    page?: number;
    page_size?: number;
    verification_status?: VerificationStatus | "";
  }) => api.list<AdminProvider>("/admin/kyc/queue", params),
  detail: (providerId: string) => api.get<KycDetail>(`/admin/kyc/${providerId}`),
  review: (providerId: string, approve: boolean, reason?: string) =>
    api.post<AdminProvider>(`/admin/kyc/${providerId}/review`, { approve, reason }),
};

export const serviceReview = {
  queue: (params: {
    page?: number;
    page_size?: number;
    review_status?: ServiceReviewStatus | "";
  }) => api.list<ReviewService>("/admin/services", params),
  review: (serviceId: string, approve: boolean, reason?: string) =>
    api.post<ReviewService>(`/admin/services/${serviceId}/review`, { approve, reason }),
};

export const tickets = {
  list: (params: {
    page?: number;
    page_size?: number;
    ticket_status?: string;
    priority?: string;
    assignee_staff_id?: string;
    unassigned?: boolean;
    overdue?: boolean;
    q?: string;
  }) => api.list<Ticket>("/admin/tickets", params),
  stats: () => api.get<TicketStats>("/admin/tickets/stats"),
  get: (id: string) => api.get<Ticket>(`/admin/tickets/${id}`),
  create: (body: {
    subject: string;
    body: string;
    priority?: string;
    channel?: string;
    requester_contact?: string | null;
    requester_user_id?: string | null;
    category?: string | null;
  }) => api.post<Ticket>("/admin/tickets", body),
  update: (id: string, body: Record<string, unknown>) =>
    api.patch<Ticket>(`/admin/tickets/${id}`, body),
  reply: (id: string, body: string, is_internal: boolean) =>
    api.post<unknown>(`/admin/tickets/${id}/messages`, { body, is_internal }),
  setStatus: (id: string, status: string, resolution?: string) =>
    api.patch<Ticket>(`/admin/tickets/${id}/status`, { status, resolution }),
  assign: (id: string, assignee_staff_id: string | null) =>
    api.patch<Ticket>(`/admin/tickets/${id}/assignee`, { assignee_staff_id }),
};

export const catalog = {
  categories: () => api.get<Category[]>("/catalog/categories", { active_only: false }),
  setMerchandising: (
    id: string,
    body: {
      display_order?: number;
      icon_url?: string | null;
      is_featured?: boolean;
      is_active?: boolean;
    },
  ) => api.patch<Category>(`/admin/catalog/categories/${id}/merchandising`, body),
};

export const homeSections = {
  list: () => api.get<HomeSection[]>("/admin/home-sections"),
  create: (body: Record<string, unknown>) =>
    api.post<HomeSection>("/admin/home-sections", body),
  update: (id: string, body: Record<string, unknown>) =>
    api.patch<HomeSection>(`/admin/home-sections/${id}`, body),
  remove: (id: string) => api.delete<void>(`/admin/home-sections/${id}`),
  reorder: (order: string[]) =>
    api.post<HomeSection[]>("/admin/home-sections/reorder", { order }),
};

export const config = {
  list: () => api.get<SettingRow[]>("/admin/config"),
  history: (key?: string) =>
    api.get<SettingHistoryRow[]>("/admin/config/history", { key, limit: 100 }),
  update: (key: string, value: unknown, reason?: string) =>
    api.put<{ key: string; applied: boolean; requires_restart: boolean }>(
      `/admin/config/${key}`,
      { value, reason },
    ),
  reset: (key: string) => api.delete<unknown>(`/admin/config/${key}`),
};

export const audit = {
  list: (params: {
    page?: number;
    page_size?: number;
    action?: string;
    target_type?: string;
    target_id?: string;
  }) => api.list<AuditEvent>("/admin/audit", params),
};

export const staff = {
  list: () => api.get<StaffAccount[]>("/admin/staff"),
  create: (body: {
    email: string;
    full_name: string;
    password: string;
    roles: string[];
  }) => api.post<StaffAccount>("/admin/staff", body),
  update: (id: string, body: Record<string, unknown>) =>
    api.patch<StaffAccount>(`/admin/staff/${id}`, body),
};

export type BoundaryShape =
  | { geojson: unknown; source?: string }
  | { circle: { latitude: number; longitude: number; radius_km: number }; source?: string };

export const serviceArea = {
  overview: () => api.get<ServiceAreaOverview>("/admin/service-area"),
  searchLocations: (q: string) =>
    api.get<GeoLocationHit[]>("/admin/service-area/locations", { q }),
  upload: (code: string, body: BoundaryShape) =>
    api.put<ServiceAreaOverview>(
      `/admin/service-area/boundaries/${encodeURIComponent(code)}`,
      body,
    ),
  remove: (code: string) =>
    api.delete<ServiceAreaOverview>(
      `/admin/service-area/boundaries/${encodeURIComponent(code)}`,
    ),
  check: (latitude: number, longitude: number) =>
    api.post<ServiceAreaCheck>("/admin/service-area/check", { latitude, longitude }),
};
